import { NoImage, RadioButton } from 'macaron-frontend-ui'
import { useCallback, useState } from 'react'
import Image from '~/components/image/Image'
import type { SeatId } from '~/lib/graphql/scalars'
import { useReserveNavigate, useSendClickEvent } from '~/navigation'
import { cva, sva } from '~/styled-system/css'
import { useDisplaySeats, useSetSeatId } from '../../store/seat'
import { useSelectedSeatId } from '../../store/stateMachine/context'
import type { DisplaySeat } from '../../store/stateMachine/statemachine'
import { Description } from './Description'

export { SeatDetailModal } from './SeatDatailModal'

const seatSelectStyles = sva({
  slots: ['container', 'title', 'seats'],
  base: {
    title: {
      width: '[100px]',
      fontSize: 'sm',
      fontWeight: 'bold',
      md: {
        fontSize: 'md',
      },
    },
  },
})

const containerStyle = cva({
  base: {
    spaceY: '2',
    rounded: 'default',
    border: 'medium',
    p: '3',
    md: {
      spaceY: '3',
      p: '4',
    },
  },
  variants: {
    length: {
      single: {
        md: {
          display: 'flex',
          spaceX: '3',
          spaceY: '0',
        },
      },
    },
  },
})

const seatsStyle = cva({
  base: {
    spaceY: '4',
    md: {
      spaceY: '0',
    },
  },
  variants: {
    length: {
      multiple: {
        md: {
          display: 'grid',
          gridTemplateColumns: '2',
          gridGap: '4',
        },
      },
    },
  },
})

export function SeatsSelect() {
  const { seats, selected, onChange } = useSeatsSelect()
  const classes = seatSelectStyles()
  return (
    <div className={seats.length <= 1 ? containerStyle({ length: 'single' }) : containerStyle()}>
      <h2 className={classes.title}>ご予約の席</h2>
      <div className={seats.length <= 1 ? seatsStyle() : seatsStyle({ length: 'multiple' })}>
        {seats.map((s) => (
          <Seat
            seat={s}
            key={s.seatId}
            selected={selected}
            onChange={onChange}
            length={seats.length}
          />
        ))}
      </div>
    </div>
  )
}

function useSeatsSelect() {
  const selected = useSelectedSeatId()
  const seats = useDisplaySeats()
  const change = useSetSeatId()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // 選択するした席の value は必ず SeatId なので型アサーション
      change(e.target.value as SeatId)
    },
    [change]
  )
  return { seats, selected, onChange }
}

const seatStyles = sva({
  slots: ['container', 'content'],
  base: {
    container: {
      display: 'flex',
      alignItems: 'start',
      spaceX: '2',
    },
    content: {
      flex: '1',
      spaceY: '1',
    },
  },
})

function Seat({
  seat,
  selected,
  onChange,
  length,
}: {
  seat: DisplaySeat
  selected?: SeatId
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  length: number
}) {
  const classes = seatStyles()
  return (
    <div className={classes.container}>
      <SeatImage seat={seat} />
      <div className={classes.content}>
        {length > 1 ? (
          <RadioButton
            onChange={onChange}
            value={seat.seatId}
            checked={selected === seat.seatId}
            disabled={seat.disabled}
          >
            {seat.label}
          </RadioButton>
        ) : (
          <p>{seat.label}</p>
        )}
        <Description seat={seat} />
        <SeatDetail seat={seat} />
      </div>
    </div>
  )
}

function SeatImage({ seat }: { seat: DisplaySeat }) {
  const { noImage, onError } = useSeatImage()
  if (seat.image === null || noImage) {
    return <NoImage width={68} height={68} />
  }
  return (
    <Image
      alt={seat.label}
      src={seat.image.url}
      className="rounded"
      onError={onError}
      width={68}
      height={68}
    />
  )
}

function useSeatImage() {
  const [noImage, setNoImage] = useState(false)
  const onError = useCallback(() => {
    setNoImage(true)
  }, [])
  return { noImage, onError }
}

function SeatDetail({ seat }: { seat: DisplaySeat }) {
  const { onClick } = useSeatDetail()
  return (
    <button type="button" onClick={onClick(seat)} className="text-sm text-accent-blue">
      席詳細
    </button>
  )
}

function useSeatDetail() {
  const navigate = useReserveNavigate()
  const sendClickEvent = useSendClickEvent()
  const onClick = useCallback(
    (seat: DisplaySeat) => {
      return () => {
        sendClickEvent({ seat: 'detail', click_event: 'ReserveInput/seat/detail' })
        navigate({ type: 'openSeatDetail', seat: seat })
      }
    },
    [navigate, sendClickEvent]
  )
  return { onClick }
}
